import { PlaceOutlined, ExpandMore } from '@mui/icons-material';
import { Box, Divider, Stack, Tooltip, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { map, random } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';

import {
  mapIconToWeatherCode,
  mockWeatherDatasets,
  weatherCodeToDescription,
} from '../ReportComponents/WeatherCard/sample';

import CurrentUserContext from '@/Contexts/CurrentUser';
import { ReportContext } from '@/Contexts/ReportContext';

const WeatherTelemetryV2: React.FC = () => {
  const { dateTimePreference } = useContext(CurrentUserContext);
  const { selectedReport } = useContext(ReportContext);
  const [currentForecast, setCurrentForecast] = useState<(typeof mockWeatherDatasets)[number]>(mockWeatherDatasets[0]);

  const indexOfCurrentForecast = currentForecast?.hourly.time.indexOf(currentForecast?.current.time);

  useEffect(() => {
    if (selectedReport) {
      setCurrentForecast(mockWeatherDatasets[random(0, mockWeatherDatasets.length - 1)]);
    }
  }, [selectedReport?.reportDate]);

  const conditionText = weatherCodeToDescription(currentForecast.current.weather_code);

  const forecastTooltipContent = (
    <Box sx={{ p: 1, minWidth: 260 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, color: '#F8FAFC', letterSpacing: '0.04em' }}>
          SITE WEATHER FORECAST · LONDON
        </Typography>
        <Typography variant="caption" sx={{ color: '#94A3B8' }}>
          {conditionText}
        </Typography>
      </Stack>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.12)', mb: 1 }} />
      <Stack direction="row" spacing={1.5} justifyContent="space-between">
        {map(new Array(4), (_, idx) => {
          const currentIndex = indexOfCurrentForecast + (idx + 1) * 2;
          return (
            <Stack
              key={`weather-forecast-${idx}`}
              alignItems="center"
              sx={{
                px: 1,
                py: 0.5,
                borderRadius: 1,
                backgroundColor: 'rgba(255,255,255,0.06)',
                minWidth: 52,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: '#CBD5E1',
                  fontSize: '0.68rem',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {dayjs(currentForecast.hourly.time[currentIndex]).format(dateTimePreference.time)}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  color: '#FFFFFF',
                  fontVariantNumeric: 'tabular-nums',
                  mt: 0.25,
                }}
              >
                {Math.round(currentForecast.hourly.temperature_2m[currentIndex])}
                &deg;C
              </Typography>
            </Stack>
          );
        })}
      </Stack>
    </Box>
  );

  return (
    <Tooltip
      title={forecastTooltipContent}
      placement="bottom"
      arrow
      slotProps={{
        tooltip: {
          sx: {
            backgroundColor: '#0F172A',
            borderRadius: 1.5,
            boxShadow: '0 10px 25px -5px rgba(15, 23, 42, 0.35)',
            maxWidth: 340,
          },
        },
        arrow: {
          sx: { color: '#0F172A' },
        },
      }}
    >
      <Box
        data-testid="weather-card-container"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1.25,
          px: 1.5,
          height: 36,
          backgroundColor: '#F8FAFC',
          border: '1px solid #E2E8F0',
          borderRadius: 1.5,
          color: '#0F172A',
          cursor: 'default',
          transition: 'all 0.15s ease',
          '&:hover': {
            backgroundColor: '#F1F5F9',
            borderColor: '#CBD5E1',
          },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              color: '#2563EB',
              fontSize: '1.1rem',
            }}
          >
            {mapIconToWeatherCode(currentForecast.current.weather_code)}
          </Box>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              color: '#0F172A',
              fontVariantNumeric: 'tabular-nums',
              lineHeight: 1,
            }}
          >
            {Math.round(currentForecast.current.apparent_temperature)}&deg;C
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: '#64748B',
              fontSize: '0.72rem',
              display: { xs: 'none', xl: 'inline' },
            }}
          >
            (Feels {Math.round(currentForecast.current.temperature_2m)}&deg;)
          </Typography>
        </Stack>

        <Divider orientation="vertical" flexItem sx={{ my: 0.75 }} />

        <Stack direction="row" alignItems="center" spacing={0.5}>
          <PlaceOutlined sx={{ fontSize: 14, color: '#64748B' }} />
          <Typography variant="caption" sx={{ color: '#334155', fontWeight: 500, fontSize: '0.75rem' }}>
            London
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: '#64748B',
              fontSize: '0.7rem',
              fontVariantNumeric: 'tabular-nums',
              ml: 0.5,
            }}
          >
            · {dayjs(currentForecast.current.time).format(dateTimePreference.time)}
          </Typography>
          <ExpandMore sx={{ fontSize: 15, color: '#94A3B8' }} />
        </Stack>
      </Box>
    </Tooltip>
  );
};

export default WeatherTelemetryV2;
